/**
 * ComparePage Component
 *
 * Wraps the existing VersionComparison component to compare
 * extraction results from two processed documents.
 * Documents are selected from the DocumentList via checkboxes.
 */

import React, { useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import { useExtraction } from '../../contexts/ExtractionContext';
import { useDocuments } from '../../contexts/DocumentContext';
import { VersionComparison } from '../../components/VersionComparison';
import type { ExtractionSummary, ExtractionResults } from '../../types/extraction';
import styles from './ComparePage.module.css';

export const ComparePage: React.FC = () => {
  const { leftId, rightId } = useParams<{ leftId: string; rightId: string }>();
  const navigate = useNavigate();
  const { fetchExtraction } = useExtraction();
  const { getDocumentById } = useDocuments();

  const leftDoc = leftId ? getDocumentById(leftId) : null;
  const rightDoc = rightId ? getDocumentById(rightId) : null;

  // Build ExtractionSummary objects from the two documents
  const extractions: ExtractionSummary[] = useMemo(() => {
    const summaries: ExtractionSummary[] = [];
    if (leftDoc) {
      summaries.push({
        id: leftDoc.processId,
        version: 1,
        processedAt: leftDoc.importedTime || leftDoc.lastModifiedTime,
        documentIds: [leftDoc.processId],
        overallConfidence: leftDoc.entityScore,
        displayName: leftDoc.processedFileName || 'Document 1',
      });
    }
    if (rightDoc) {
      summaries.push({
        id: rightDoc.processId,
        version: 2,
        processedAt: rightDoc.importedTime || rightDoc.lastModifiedTime,
        documentIds: [rightDoc.processId],
        overallConfidence: rightDoc.entityScore,
        displayName: rightDoc.processedFileName || 'Document 2',
      });
    }
    return summaries;
  }, [leftDoc, rightDoc]);

  // Load extraction results by process ID — returns results directly
  const loadExtraction = useCallback(
    async (extractionId: string): Promise<ExtractionResults | null> => {
      return fetchExtraction(extractionId);
    },
    [fetchExtraction]
  );

  const handleClose = useCallback(() => {
    navigate('/evidence-extractor');
  }, [navigate]);

  if (!leftId || !rightId) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p>Two documents must be selected for comparison.</p>
          <Link to="/evidence-extractor" className={styles.backButton}>
            Back to Documents
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <Link to="/evidence-extractor" className={styles.backLink}>
          <ArrowLeftRegular className={styles.backIcon} />
          <span>Back to Documents</span>
        </Link>
        <h1 className={styles.title}>Compare Documents</h1>
        <p className={styles.subtitle}>
          Comparing extraction results between{' '}
          <strong>{leftDoc?.processedFileName || 'Document 1'}</strong> and{' '}
          <strong>{rightDoc?.processedFileName || 'Document 2'}</strong>
        </p>
      </header>

      <VersionComparison
        extractions={extractions}
        loadExtraction={loadExtraction}
        onClose={handleClose}
        initialLeftVersionId={leftId}
        initialRightVersionId={rightId}
      />
    </div>
  );
};

export default ComparePage;
